import type { ActorSyncGatewayRefusal } from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";
import type { ActorSyncGatewayAnswer } from "#actor-sync-fetch";

/**
 * Why the sync gateway answered a request itself instead of serving it, plus the one refusal that
 * has no reason header: an upload it will not read because it is too large.
 */
export type ActorSyncRefusal = ActorSyncGatewayRefusal | "too-large";

const REFUSALS: readonly ActorSyncRefusal[] = [
  "credential",
  "database-moved",
  "server-behind",
  "too-large",
  "unavailable",
  "update-required",
];
const ERROR_NAME = "ActorSyncRefusedError";
const TOO_LARGE_STATUS = 413;

/**
 * A push or pull failed because the gateway refused one of its requests. `code` says why. Every
 * reason has an automatic response (see `database.md`, "Device: sync supervisor"); none of them
 * means the replica is damaged.
 *
 * The reason travels as `code` because that is what survives the web leader's message boundary
 * along with the name; read it with {@link readActorSyncRefusal}, never with `instanceof`.
 */
export class ActorSyncRefusedError extends Error {
  override readonly name = ERROR_NAME;
  readonly code: ActorSyncRefusal;

  constructor(code: ActorSyncRefusal, cause: unknown) {
    super(`Cloud sync was refused (${code})`, { cause });
    this.code = code;
  }
}

/** The gateway's reason when `error` is a refused sync step, on either side of the web leader. */
export function readActorSyncRefusal(error: unknown): ActorSyncRefusal | null {
  if (!(error instanceof Error) || error.name !== ERROR_NAME) {
    return null;
  }
  const code: unknown = Reflect.get(error, "code");
  return REFUSALS.find((refusal) => refusal === code) ?? null;
}

/**
 * Remembers what the gateway said during one sync step, so the step's failure can be named. The
 * engine reports a refused request as an error of its own with the status buried in text; the
 * answer the transport saw is the reliable source. Sync steps run one at a time, so one slot does.
 */
export function createActorSyncStepTracker(): {
  onAnswer(answer: ActorSyncGatewayAnswer): void;
  run<T>(step: () => Promise<T>): Promise<T>;
} {
  let refusal: ActorSyncRefusal | null = null;
  return {
    onAnswer: (answer) => {
      refusal = answer.refusal ?? (answer.status === TOO_LARGE_STATUS ? "too-large" : refusal);
    },
    run: async (step) => {
      refusal = null;
      try {
        return await step();
      } catch (error) {
        if (refusal !== null) {
          throw new ActorSyncRefusedError(refusal, error);
        }
        throw error;
      }
    },
  };
}
